import React from 'react';
import {
  Action,
  ConfirmModal,
  ICON_CLOSE,
  Portal,
  useModal,
  useNotifications,
} from '@doveit/bricks';
import { updateContentsSelection } from '../../../providers/api/content/contentProvider';
import { Content } from '../../../providers/api/dtos';

export const DESELECT_SHOOTING_PHOTO_SUCCESS_MESSAGE = 'Foto deselezionata con successo.';
export const DESELECT_SHOOTING_PHOTO_ERROR_MESSAGE = 'Errore durante la deselezione della foto.';

interface ChildFnProps {
  deselectPhoto: VoidFunction;
  disabled: boolean;
  loading: boolean;
}

export interface DeselectShootingPhotoActionProps {
  /** The gallery photo to be deselected */
  photo: Content;
  disabled?: boolean;
  children?: (props: ChildFnProps) => React.ReactNode;
  onSuccess?: (payload: Content[]) => void;
}

const DeselectShootingPhotoAction: React.FC<DeselectShootingPhotoActionProps> = ({
  photo,
  disabled = false,
  children,
  onSuccess,
}) => {
  const confirmModal = useModal();
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const deselectPhoto = React.useCallback(() => {
    confirmModal.open();
  }, [confirmModal]);

  const handleConfirm = async () => {
    try {
      setIsSaving(true);

      const payload: Content[] = [{ ...photo, selected: false }];

      await updateContentsSelection(payload);
      onSuccess?.(payload);
      confirmModal.close();
      addSuccess(DESELECT_SHOOTING_PHOTO_SUCCESS_MESSAGE);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      addError(DESELECT_SHOOTING_PHOTO_ERROR_MESSAGE);
    } finally {
      setIsSaving(false);
    }
  };

  // Only the gallery photos selected from a shooting can be deselected
  if (!photo.shootingId) {
    return null;
  }

  const isDisabled = disabled || isSaving;

  return (
    <>
      {children
        ? children({ deselectPhoto, disabled: isDisabled, loading: isSaving })
        : (
          <Action
            label="Deseleziona foto"
            onClick={deselectPhoto}
            disabled={isDisabled}
            loading={isSaving}
            iconLeft={{ path: ICON_CLOSE }}
            size="S"
            aria-label="Pulsante per deselezionare la foto dello shooting"
          />
        )}
      <Portal>
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          saving={isSaving}
          title="Confermi di deselezionare la foto?"
          aria-label="Modale di conferma deselezione foto"
          onConfirm={handleConfirm}
          onAbort={confirmModal.close}
        >
          Deselezionando la foto non sarà più visibile nella galleria dell&apos;immobile. Potrai in ogni momento riselezionarla a partire dallo shooting.
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default DeselectShootingPhotoAction;
